import React, { useEffect, useRef, useState } from 'react';
import { useAuthContext } from 'react-oauth2-code-pkce';
import { getUser, saveUser } from '@utils/localStorage';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '@store/reducers/authentication';
import { history } from '@utils/history';
import authAPI from '@api/oneaccount';
import { UnAuthorizePage } from '@components/unauthorize/unAuthorizePage';

export const AuthTokenHandler = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const { token, tokenData, logIn, error } = useAuthContext();
  const dispatch = useDispatch();
  const hasBootstrappedRef = useRef(false);
  const isSyncingRef = useRef(false);

  useEffect(() => {
    if (!token || !tokenData) return;
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;

    if (hasBootstrappedRef.current) {
      const existing = getUser();
      if (existing) {
        saveUser({ ...existing, token });
      }
    }

    const syncAuthData = async () => {
      const userInfoResponse = await authAPI.userInfo({
        headers: { Authorization: `Bearer ${token}` },
      });

      const user = {
        name: tokenData.name || tokenData.preferred_username || tokenData.email,
        email: tokenData.email,
        token,
        scopes: userInfoResponse?.scopes || [],
      };

      saveUser(user);
      dispatch(loginSuccess(user));

      if (!hasBootstrappedRef.current) {
        hasBootstrappedRef.current = true;
        setIsReady(true);
        history.push('/dashboard');
      }
    };

    syncAuthData()
      .catch((err) => {
        console.error('Error syncing auth data', err);
        if (err.response && err.response.status === 403) {
          setIsForbidden(true);
        } else {
          logIn();
        }
      })
      .finally(() => {
        isSyncingRef.current = false;
      });

    return () => {
    };
  }, [token, tokenData, dispatch, logIn]);

  // Handle auth errors from the library
  useEffect(() => {
    if (error) {
      console.error('Auth error:', error);
      logIn();
    }
  }, [error, logIn]);

  if (isForbidden) return <UnAuthorizePage />;

  if (!token || !isReady) return null;

  return <>{children}</>;
};
